import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Info, CheckCircle, AlertTriangle, XCircle, X } from "lucide-react";
import { cx } from "./cx";

// Toast system.
//   <ToastProvider> wraps an app shell (CustomerApp / DriverApp / OpsApp).
//   const toast = useToast(); toast({ tone, message, duration? })
//   tone: info|success|warning|danger. Auto-dismisses (default 4s), stacked
//   bottom-right via a portal.
//
// REFINEMENT NOTES:
// - Each toast gets a 3px tone-colored left edge (border-l-[3px]) so the tone
//   reads at a glance without changing the white card.
// - Dismiss button upgraded to focus-visible.
const ToastContext = createContext(null);

const TONES = {
  info: { wrap: "border-info/30 text-info", Icon: Info },
  success: { wrap: "border-success/30 text-success", Icon: CheckCircle },
  warning: { wrap: "border-accent/40 text-primary", Icon: AlertTriangle },
  danger: { wrap: "border-danger/30 text-danger", Icon: XCircle },
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    ({ tone = "info", message, duration = 4000 } = {}) => {
      const id = ++idRef.current;
      setToasts((list) => [...list, { id, tone, message }]);
      if (duration) setTimeout(() => dismiss(id), duration);
      return id;
    },
    [dismiss]
  );

  return (
    <ToastContext.Provider value={toast}>
      {children}
      {createPortal(
        <div className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2 w-80 max-w-[calc(100vw-2rem)]">
          {toasts.map((t) => {
            const { wrap, Icon } = TONES[t.tone] || TONES.info;
            return (
              <div
                key={t.id}
                role="status"
                className={cx(
                  "flex items-start gap-3 rounded-card border border-l-[3px] bg-white shadow-pop px-4 py-3 text-sm animate-fade-in",
                  wrap
                )}
              >
                <Icon size={18} className="mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0 text-body">{t.message}</div>
                <button
                  onClick={() => dismiss(t.id)}
                  aria-label="Dismiss"
                  className="shrink-0 -mr-1 rounded-btn p-0.5 text-body/50 transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <X size={16} />
                </button>
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // No provider mounted — fail soft so a stray call never crashes a page.
    return () => {};
  }
  return ctx;
}
